import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { useApp } from './state/AppState.jsx';
import { useRoute, navigate } from './lib/router.js';
import { Toasts, Loading } from './components/ui.jsx';
import * as Icon from './lib/icons.jsx';
import Shell from './components/Shell.jsx';
import AuthPage from './pages/AuthPage.jsx';
import Dashboard from './pages/Dashboard.jsx';
/* Everything below the fold of a sign-in is fetched when first visited, not on
   every load. Sign-in lands on the Dashboard, so that one stays eager; the
   board page - the heaviest chunk by far, since it carries five views and the
   item panel - arrives only when a board is opened. The chunks are cached, so
   each page costs its wire time exactly once per deploy. */
const BoardPage = lazy(() => import('./pages/BoardPage.jsx'));
const MyWork = lazy(() => import('./pages/MyWork.jsx'));
const AllBoards = lazy(() => import('./pages/AllBoards.jsx'));
const Reports = lazy(() => import('./pages/Reports.jsx'));
const People = lazy(() => import('./pages/People.jsx'));
const Chats = lazy(() => import('./pages/Chats.jsx'));
const Notes = lazy(() => import('./pages/Notes.jsx'));
const Settings = lazy(() => import('./pages/Settings.jsx'));
const ResetPassword = lazy(() => import('./pages/ResetPassword.jsx'));

const PUBLIC_ROUTES = ['/login', '/signup'];
const isPublic = (path) => PUBLIC_ROUTES.includes(path) || path.startsWith('/invite/');

export default function App() {
  const { booting, bootError, user, recovering, toasts, dismissToast, signOut } = useApp();
  const route = useRoute();

  // Route guard: bounce signed-out visitors to sign-in, and signed-in ones off it.
  useEffect(() => {
    if (booting) return;
    if (!user && !isPublic(route.path)) navigate('/login', { replace: true });
    if (user && PUBLIC_ROUTES.includes(route.path)) navigate('/', { replace: true });
  }, [booting, user, route.path]);

  if (booting) {
    return <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}><Loading label="Starting Pulse" /></div>;
  }

  // A session that cannot load its account used to render nothing at all: no
  // error, no sign-out button, just a black page. Always give a way out.
  if (bootError) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100%', padding: 24 }}>
        <div className="card" style={{ maxWidth: 460, textAlign: 'center' }}>
          <div className="card-body grid" style={{ gap: 12 }}>
            <Icon.Alert size={26} style={{ margin: '0 auto', color: 'var(--danger)' }} />
            <h1 style={{ fontSize: 17, margin: 0 }}>Could not load your account</h1>
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0 }}>{bootError}</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button type="button" className="btn" onClick={() => window.location.reload()}>
                Try again
              </button>
              <button type="button" className="btn primary" onClick={signOut}>
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // A reset link signs you in before you have chosen the new password, so this
  // has to come before the normal signed-in render or the app would drop them
  // on the dashboard and forget why they clicked.
  if (user && recovering) {
    return (
      <>
        <Suspense fallback={<Loading />}>
          <ResetPassword />
        </Suspense>
        <Toasts toasts={toasts} onDismiss={dismissToast} />
      </>
    );
  }

  if (!user) {
    return (
      <>
        <AuthPage route={route} />
        <Toasts toasts={toasts} onDismiss={dismissToast} />
      </>
    );
  }

  return (
    <>
      <Shell route={route}>
        <Suspense fallback={<Loading />}>
          <Routed route={route} />
        </Suspense>
      </Shell>
      <Toasts toasts={toasts} onDismiss={dismissToast} />
    </>
  );
}

function Routed({ route }) {
  const board = route.match('/boards/:id');
  if (board) return <BoardPage boardId={board.id} route={route} />;

  // An already-signed-in user clicking an invite link used to fall through to
  // the dashboard, silently discarding the invite: /invite/:token is public but
  // is not in PUBLIC_ROUTES, so the guard does not redirect and nothing
  // redeemed it.
  const invite = route.match('/invite/:token');
  if (invite) return <InviteRedeemer token={invite.token} />;

  // /chats and /chats/:id are one page: the list is always there, and the id
  // only says which thread is open beside it.
  const chat = route.match('/chats/:id');
  if (chat) return <Chats conversationId={chat.id} />;

  // Same shape as chats: the tree is always there, the id says which page
  // is open beside it.
  const note = route.match('/notes/:id');
  if (note) return <Notes noteId={note.id} route={route} />;

  switch (route.path) {
    case '/chats': return <Chats />;
    case '/notes': return <Notes route={route} />;
    case '/my-work': return <MyWork />;
    case '/boards': return <AllBoards />;
    case '/reports': return <Reports />;
    case '/people': return <People />;
    case '/settings': return <Settings />;
    default: return <Dashboard />;
  }
}

/**
 * Redeems an invite for someone who is already signed in, then drops them into
 * the workspace they were invited to. accept_invite rejects a token issued to a
 * different address, so a link forwarded to the wrong person fails here rather
 * than quietly joining them.
 */
function InviteRedeemer({ token }) {
  const app = useApp();
  const [error, setError] = useState(null);
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    (async () => {
      try {
        await app.acceptInvite(token);
        app.toast('You have joined the workspace', 'success');
        navigate('/', { replace: true });
      } catch (err) {
        setError(err.message);
      }
    })();
  }, [token, app]);

  if (error) {
    return (
      <div className="scroll-y">
        <div className="page" style={{ maxWidth: 520 }}>
          <h1 className="page-title">That invite did not work</h1>
          <p className="page-sub">{error}</p>
          <button type="button" className="btn" onClick={() => navigate('/', { replace: true })}>
            Go to your dashboard
          </button>
        </div>
      </div>
    );
  }
  return <Loading label="Joining the workspace" />;
}
